<template>
  <fragment>
    <b-row>
      <b-col md="12">
        <CreateReviewForm
          @onSubmit="createReviewForm"
          :rwSessions="listReviewSession"
          :users="listUser"
          :group-criteria="listGroupCriteria"
        />
      </b-col>

      <b-col md="12">
        <Heading1 text="List of sessions">
          <Loader v-if="loading" size="2rem" />
          <b-button v-else size="lg" variant="light" @click="refresh">
            <b-icon-arrow-repeat />
          </b-button>
        </Heading1>
      </b-col>
    </b-row>

    <b-row>
      <b-col>
        <b-form-group label="Filter" label-for="filterInput">
          <b-input-group size="sm">
            <b-form-input v-model="filter" type="search" id="filterInput" placeholder="Type to Search"></b-form-input>
            <b-input-group-append>
              <b-button :disabled="!filter" @click="filter = ''">Clear</b-button>
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
      </b-col>

      <b-col>
        <b-form-group label="Filter On" description="Leave all unchecked to filter on all data">
          <b-form-checkbox-group v-model="filterOn">
            <b-form-checkbox value="target">Target</b-form-checkbox>
            <b-form-checkbox value="rwSession">Session</b-form-checkbox>
            <b-form-checkbox value="updater">Author</b-form-checkbox>
          </b-form-checkbox-group>
        </b-form-group>
      </b-col>
    </b-row>

    <b-table
      striped
      hover
      responsive="sm"
      v-if="!loading"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :fields="reviewFormFields"
      :items="listReviewForm"
      :filter="filter"
      :filterIncludedFields="filterOn"
    >
      <!-- A virtual column -->
      <template v-slot:cell(id)="data">
        <router-link :to="`/rw-form/${data.value}`">
          open
        </router-link>
      </template>

      <template v-slot:cell(target)="data">
        {{ data.value.name }}
      </template>

      <template v-slot:cell(status)="data">
        <StatusView :status="data.value" />
      </template>

      <template v-slot:cell(updater)="data">
        <span class="small">{{ data.value.name }}</span>
      </template>

      <template v-slot:cell(rwSession)="data">
        <span class="small">{{ data.value.period }}</span>
      </template>

      <template v-slot:cell(updatedAt)="data">
        {{ fmtDate(data.item.updatedAt) }}
      </template>

      <template v-slot:cell(actions)="data">
        <ActionsView
          :status="data.item.status"
          :isAdmin="userNodeId == data.item.updater.id"
          @deleteItem="() => deleteReviewForm(data.item.id)"
        />
      </template>
    </b-table>
  </fragment>
</template>

<script>
import CreateReviewForm from '@/components/CreateReviewForm';
import api from '../api';
import { mapActions, mapGetters } from 'vuex';
import utils from '../utils';
import StatusView from '@/components/StatusView';
import ActionsView from '@/components/ActionsView';
import Loader from '@/components/Loader';
import Heading1 from '@/components/Heading1';
import { ReviewSessionStatus } from '@/constants';

export default {
  name: 'ManagingReviewForm',
  components: { ActionsView, StatusView, CreateReviewForm, Loader, Heading1 },
  data() {
    return {
      ReviewSessionStatus,
      filter: null,
      filterOn: ['target', 'rwSession'],
      sortBy: 'rwSession',
      sortDesc: true,
      reviewFormFields: [
        {
          key: 'id',
          label: '',
        },
        {
          key: 'target',
          sortable: true,
        },
        {
          key: 'status',
          sortable: true,
        },
        {
          key: 'updater',
          label: 'Author',
          sortable: true,
        },
        {
          key: 'rwSession',
          label: 'Session',
          sortable: true,
        },
        {
          key: 'updatedAt',
          label: 'Last update',
        },
        'actions',
      ],
      loading: false,
    };
  },
  computed: {
    ...mapGetters('review', ['listReviewSession', 'listReviewForm']),
    ...mapGetters('user', ['listUser']),
    ...mapGetters('criteria', ['listGroupCriteria']),
    ...mapGetters('app', ['isAdmin', 'userNodeId']),
  },
  methods: {
    ...mapActions('review', ['fetchListReviewSession', 'fetchListReviewForm', 'deleteReviewForm']),
    ...mapActions('user', ['fetchListUser']),
    ...mapActions('criteria', ['fetchListGroupCriteria']),
    refresh() {
      this.loading = true;
      this.fetchListReviewForm().then(() => (this.loading = false));
    },
    createReviewForm(formData) {
      api
        .gql({
          query: `mutation createReviewForm($input: createReviewFormInput!) {
            createReviewForm(input: $input) {
              out {
                id
              }
            }
          }`,
          variables: {
            input: {
              reviewSessionId: utils.fromGlobalId(formData.reviewSessionId).id,
              target: utils.fromGlobalId(formData.target).id,
              groupCriteriaId: utils.fromGlobalId(formData.groupCriteriaId).id,
            },
          },
        })
        .then(({ createReviewForm: { out } }) => {
          console.log('createReviewForm -> out', out);
          this.$toastr.s(JSON.stringify(out, null, 2), 'Created Review Form');
          this.$router.push({ name: 'Performing Review Form', params: { id: out.id } });
        });
    },
  },
  mounted() {
    // get list of forms
    this.refresh();
    this.fetchListReviewSession({
      status: this.ReviewSessionStatus.DOING,
    });
    this.fetchListUser();
    this.fetchListGroupCriteria();
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/css/colors';

.headline {
  margin-bottom: 2rem;

  display: flex;

  h1 {
    font-size: 2rem;
    margin-right: 1rem;
    color: $darkBlue;
  }
}

td > a {
  color: $darkBlue;
  text-decoration: underline;
}
</style>
